import classnames from 'classnames'
import throttle from 'lodash.throttle'
import * as React from 'react'

import * as hoverChart from '../../../shared/hover-chart'
import TilesStore from '../../stores/tilesStore'
import Sparkline from '../CanvasSparkline'

interface MapTileProps {
  ticker: string
  showSparkline: boolean
  hoverOffsetX: number
  hoverOffsetY: number
  colorScale: (d: number) => string
}

function getTileState(ticker: string, prevWidth = 0) {
  var tile = TilesStore.getTile(ticker)
  var group = TilesStore.getTilesGroup(tile.ticker).toLowerCase()

  return {
    ...tile,
    group,
    width: prevWidth,
  }
}

export class MapTile extends React.Component<React.PropsWithChildren<MapTileProps>> {
  state = getTileState(this.props.ticker)

  resizeObserver?: ResizeObserver
  containerRef = React.createRef<HTMLAnchorElement>()

  componentDidMount() {
    TilesStore.addChangeListener(this._onChange)

    // Resize sparkline on window resize
    const container = this.containerRef.current
    if (container && this.state.sparkline) {
      this.setState({ width: container.getBoundingClientRect().width }, () => {
        this.resizeObserver = new ResizeObserver(
          throttle(([entry]: ResizeObserverEntry[]) => {
            this.setState({ width: entry.contentRect.width })
          }, 100)
        )
        this.resizeObserver.observe(container)
      })
    }
  }

  componentWillUnmount() {
    TilesStore.removeChangeListener(this._onChange)
    this.resizeObserver?.disconnect()
  }

  _onChange = () => {
    this.setState(getTileState(this.props.ticker, this.state.width))
  }

  render() {
    const { colorScale, children, showSparkline, hoverOffsetX, hoverOffsetY } = this.props
    var { ticker, width, label, last, prevClose, change, high, low, sparkline, sparklineDateChanges } = this.state

    var format = (n: number) => (n >= 0 ? '+' : '') + n.toFixed(2) + '%'
    var places = last > 6 ? 2 : 4
    var round = (n: number, plusSign?: string) =>
      (n >= 0 ? plusSign || '' : '') + (+(Math.round(Number(n + 'e+' + places)) + 'e-' + places)).toFixed(places)

    var range = (high - low) * 0.1
    var highlightHigh = last > high - range
    var highlightLow = last < low + range
    const backgroundColor = colorScale(change)

    const chartImage = hoverChart.getHoverChartImg(
      '@' + ticker.toLowerCase(),
      false,
      FinvizSettings.hoverCharts?.futures?.timeframe,
      FinvizSettings.hoverCharts?.futures?.dateRange
    )

    const boxover = `cssbody=[hoverchart] cssheader=[tabchrthdr] body=[${chartImage.img}] offsetx=[${hoverOffsetX}] offsety=[${hoverOffsetY}] delay=[100]`

    if (showSparkline) {
      return (
        <a
          ref={this.containerRef}
          className="font-3xs w-full overflow-hidden font-sans text-3xs text-white font-medium hover:text-white"
          href={'/futures_charts.ashx?p=d1&t=' + ticker}
          style={{ backgroundColor, textShadow: '1px 1px 0px rgba(34, 38, 47, 0.8)' }}
          data-boxover={boxover}
        >
          {children}
          <div className="w-full whitespace-nowrap p-1 xl:p-1.5 xl:pb-1">
            <div className="flex w-full items-baseline justify-between overflow-hidden">
              <div className="grow truncate uppercase leading-none xl:text-2xs xl:leading-none">{label}</div>
              <div className="shrink-0 pl-1 leading-none">{round(last - prevClose, '+')}</div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold xl:text-base">{round(last)}</div>
              <div className="pl-1 leading-none">({format(change)})</div>
            </div>
          </div>
          <div className="w-full overflow-hidden">
            {showSparkline && (
              <Sparkline
                key={width}
                width={Math.ceil(width)}
                height={58}
                data={sparkline}
                dateChanges={sparklineDateChanges}
                change={change}
                changeDomain={1.3}
                changeOpacity={false}
                shadow={true}
              />
            )}
          </div>
        </a>
      )
    }

    return (
      <a
        className="flex w-full flex-col items-start p-1 font-sans text-3xs text-white font-medium hover:text-white xl:p-1.5 xl:pb-1"
        href={'/futures_charts.ashx?p=d1&t=' + ticker}
        style={{ backgroundColor, textShadow: '1px 1px 0px rgba(34, 38, 47, 0.8)' }}
        data-boxover={boxover}
      >
        {children}
        <div className="uppercase leading-none xl:text-2xs xl:leading-none">{label}</div>
        <div className="text-xl font-bold xl:text-2xl">{round(last)}</div>
        <span className="text-2xs font-medium">
          {round(last - prevClose, '+')} ({format(change)})
        </span>
        <div className="flex w-full flex-col items-start tabular-nums leading-none">
          <div
            className={classnames('-mx-0.5 rounded-sm p-0.5', { 'bg-yellow-200': highlightHigh })}
            style={highlightHigh ? { textShadow: 'none' } : undefined}
          >
            <span className={classnames('inline-block w-2.5 pr-0.5 font-normal', { 'text-gray-600': highlightHigh })}>
              H
            </span>
            <span className={classnames({ 'text-gray-800': highlightHigh })}>{round(high)}</span>
          </div>
          <div
            className={classnames('-mx-0.5 rounded-sm p-0.5', { 'bg-yellow-200': highlightLow })}
            style={highlightLow ? { textShadow: 'none' } : undefined}
          >
            <span className={classnames('inline-block w-2.5 pr-0.5 font-normal', { 'text-gray-600': highlightLow })}>
              L
            </span>
            <span className={classnames({ 'text-gray-800': highlightLow })}>{round(low)}</span>
          </div>
        </div>
      </a>
    )
  }
}
